import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  CoinPackageSummary,
  CoinPurchaseQuoteResponse,
  ComplianceStatusResponse,
  ModePassSummary,
  ModePassTier,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { formatActorName, formatCoins, formatUsdCents } from '@fetmode/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/index.js';

type PurchaseMode = 'coins' | 'usd';

function ModePassCard(): ReactElement {
  const { api } = useAuth();
  const [pass, setPass] = useState<ModePassSummary | null>(null);
  const [referredBy, setReferredBy] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await api.getModePass();
    setPass(result.pass);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.wallet.modePass.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function subscribe(tier: ModePassTier): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.subscribeModePass({
        tier,
        ...(referredBy.trim() ? { referredByUsername: referredBy.trim() } : {}),
      });
      setPass(result.pass);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.wallet.modePass.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="fm-card">
        <p style={{ margin: 0, color: 'var(--fm-color-text-muted)' }}>
          {t('common.wallet.modePass.loading')}
        </p>
      </div>
    );
  }

  return (
    <div className="fm-card">
      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem' }}>{t('common.wallet.modePass.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.wallet.modePass.hint')}
      </p>
      {error ? <p className="fm-error">{error}</p> : null}
      {pass ? (
        <div>
          <p style={{ margin: 0, fontWeight: 600 }}>
            {t('common.wallet.modePass.active')
              .replace('{{tier}}', pass.tier)
              .replace('{{end}}', new Date(pass.currentPeriodEnd).toLocaleDateString())}
          </p>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
            {t('common.wallet.modePass.benefits')
              .replace('{{content}}', String(pass.benefits.contentPurchaseDiscountPct))
              .replace('{{coins}}', String(pass.benefits.coinPurchaseBonusPct))}
          </p>
          <div style={{ marginTop: '0.75rem' }}>
            <Button
              label={isSaving ? t('common.wallet.modePass.saving') : t('common.wallet.modePass.renew')}
              disabled={isSaving}
              onClick={() => {
                void subscribe(pass.tier);
              }}
            />
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <label style={{ fontSize: '0.875rem' }}>
            {t('common.wallet.modePass.referral')}
            <input
              type="text"
              value={referredBy}
              onChange={(event) => setReferredBy(event.target.value)}
              placeholder={t('common.wallet.modePass.referralPlaceholder')}
              style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
            />
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <Button
              label={t('common.wallet.modePass.subscribePlus')}
              disabled={isSaving}
              onClick={() => {
                void subscribe('PLUS');
              }}
            />
            <Button
              label={t('common.wallet.modePass.subscribePremium')}
              disabled={isSaving}
              onClick={() => {
                void subscribe('PREMIUM');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function WalletPage(): ReactElement {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [wallet, setWallet] = useState<Awaited<ReturnType<typeof api.getWallet>> | null>(null);
  const [packages, setPackages] = useState<CoinPackageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<PurchaseMode>('usd');
  const [amount, setAmount] = useState('9.99');
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [quote, setQuote] = useState<CoinPurchaseQuoteResponse | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [compliance, setCompliance] = useState<ComplianceStatusResponse | null>(null);

  const loadWallet = useCallback(async () => {
    const [result, complianceStatus, packageList] = await Promise.all([
      api.getWallet(),
      api.getComplianceStatus(),
      api.listCoinPackages(),
    ]);
    setWallet(result);
    setCompliance(complianceStatus);
    setPackages(packageList.packages);
  }, [api]);

  const refreshCompliance = useCallback(async (): Promise<ComplianceStatusResponse | null> => {
    try {
      const status = await api.getComplianceStatus();
      setCompliance(status);
      return status;
    } catch {
      return null;
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;

    void loadWallet()
      .catch(() => {
        if (!cancelled) {
          setError('Unable to load wallet balances.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadWallet, location.pathname]);

  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        void refreshCompliance();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshCompliance]);

  useEffect(() => {
    if (!compliance?.canAccess) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    if (selectedPackageId) {
      let cancelled = false;
      setIsQuoting(true);
      void api
        .quoteCoinPurchase({ packageId: selectedPackageId })
        .then((result) => {
          if (!cancelled) {
            setQuote(result);
            setQuoteError(null);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setQuote(null);
            setQuoteError(
              err instanceof ApiError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : 'Invalid package',
            );
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsQuoting(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    const parsed =
      mode === 'usd'
        ? Math.round(Number.parseFloat(amount) * 100)
        : Number.parseInt(amount, 10);

    if (!Number.isFinite(parsed) || parsed <= 0) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    let cancelled = false;
    setIsQuoting(true);

    void api
      .quoteCoinPurchase(mode === 'usd' ? { usdCents: parsed } : { coins: parsed })
      .then((result) => {
        if (!cancelled) {
          setQuote(result);
          setQuoteError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setQuote(null);
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Invalid purchase amount';
          setQuoteError(message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsQuoting(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [amount, api, compliance?.canAccess, mode, selectedPackageId]);

  const handlePurchase = async (): Promise<void> => {
    if (!quote) {
      return;
    }

    setIsPurchasing(true);
    setError(null);

    try {
      const status = await refreshCompliance();
      if (!status?.canAccess) {
        setError('Age verification is required before buying coins.');
        return;
      }
      const result = await api.createCoinPurchase(
        selectedPackageId
          ? { packageId: selectedPackageId }
          : mode === 'usd'
            ? { usdCents: quote.usdCents }
            : { coins: quote.coins },
      );
      void navigate(`/wallet/purchase/${result.purchaseId}`);
    } catch {
      setError('Unable to start checkout.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
    color: 'var(--fm-color-text-muted)',
  } as const;

  return (
    <section>
      <p style={{ color: 'var(--fm-color-text-muted)', marginTop: 0 }}>
        {t('common.auth.signedInAs')}{' '}
        {user
          ? formatActorName({
              username: user.username,
              displayName: user.displayName,
              email: user.email,
            })
          : ''}
      </p>

      {isLoading && <p style={{ color: 'var(--fm-color-text-muted)' }}>Loading balances…</p>}
      {error && <p className="fm-error">{error}</p>}

      {wallet && (
        <>
          <div className="fm-card">
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
              Spend balance
            </p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem' }}>
              {formatCoins(wallet.spend.total)}
            </p>
            <div style={rowStyle}>
              <span>Purchased</span>
              <span>{formatCoins(wallet.spend.purchased)}</span>
            </div>
            <div style={rowStyle}>
              <span>Bonus</span>
              <span>{formatCoins(wallet.spend.bonus)}</span>
            </div>
            <div style={rowStyle}>
              <span>Gifted</span>
              <span>{formatCoins(wallet.spend.gifted)}</span>
            </div>
          </div>

          {wallet.earn && user?.role === 'MODEL' && (
            <div className="fm-card">
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
                Earn balance
              </p>
              <div style={rowStyle}>
                <span>Available</span>
                <span>{formatUsdCents(wallet.earn.availableCents)}</span>
              </div>
              <div style={rowStyle}>
                <span>Pending</span>
                <span>{formatUsdCents(wallet.earn.pendingCents)}</span>
              </div>
            </div>
          )}

          <ModePassCard />

          <div className="fm-card">
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>Buy coins</h2>

            {compliance && !compliance.canAccess && (
              <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                Verification required for your region ({compliance.jurisdictionCode}).{' '}
                <Link to="/verify">Complete verification</Link>
              </p>
            )}

            {compliance?.canAccess && (
              <>
                {packages.length > 0 ? (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <p
                      style={{
                        margin: '0 0 0.5rem',
                        fontSize: '0.875rem',
                        color: 'var(--fm-color-text-muted)',
                      }}
                    >
                      Packages
                    </p>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(9.5rem, 1fr))',
                        gap: '0.5rem',
                      }}
                    >
                      {packages.map((pkg) => {
                        const selected = selectedPackageId === pkg.id;
                        return (
                          <button
                            key={pkg.id}
                            type="button"
                            onClick={() => setSelectedPackageId(pkg.id)}
                            style={{
                              textAlign: 'left',
                              padding: '0.75rem',
                              borderRadius: '0.5rem',
                              border: selected
                                ? '2px solid var(--fm-color-accent)'
                                : '1px solid var(--fm-color-border)',
                              background: 'var(--fm-color-surface)',
                              color: 'var(--fm-color-text)',
                              cursor: 'pointer',
                              font: 'inherit',
                            }}
                          >
                            <strong style={{ display: 'block' }}>{pkg.label}</strong>
                            <span style={{ fontSize: '0.875rem' }}>
                              {formatCoins(pkg.totalCoins)}
                            </span>
                            <span
                              style={{
                                display: 'block',
                                fontSize: '0.8125rem',
                                color: 'var(--fm-color-text-muted)',
                                marginTop: '0.25rem',
                              }}
                            >
                              {formatUsdCents(pkg.usdCents)}
                              {pkg.bonusPercent > 0 ? ` · +${String(pkg.bonusPercent)}%` : ''}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {selectedPackageId ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPackageId(null)}
                        style={{
                          marginTop: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--fm-color-link)',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                          fontSize: '0.8125rem',
                        }}
                      >
                        Use custom amount instead
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {!selectedPackageId ? (
                  <>
                    <div className="fm-segment-group">
                      <button
                        type="button"
                        className={mode === 'usd' ? 'fm-segment fm-segment--active' : 'fm-segment'}
                        onClick={() => {
                          setMode('usd');
                        }}
                      >
                        USD
                      </button>
                      <button
                        type="button"
                        className={
                          mode === 'coins' ? 'fm-segment fm-segment--active' : 'fm-segment'
                        }
                        onClick={() => {
                          setMode('coins');
                        }}
                      >
                        Coins
                      </button>
                    </div>
                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                      {mode === 'usd' ? 'Amount (USD)' : 'Coins'}
                      <input
                        type="number"
                        className="fm-input"
                        min={mode === 'usd' ? '1.99' : '1'}
                        step={mode === 'usd' ? '0.01' : '1'}
                        value={amount}
                        onChange={(event) => {
                          setAmount(event.target.value);
                        }}
                      />
                    </label>
                  </>
                ) : null}

                {isQuoting && (
                  <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                    Calculating quote…
                  </p>
                )}
                {quoteError && (
                  <p className="fm-error" style={{ fontSize: '0.875rem' }}>
                    {quoteError}
                  </p>
                )}
                {quote && !quoteError && (
                  <div style={{ ...rowStyle, marginTop: '1rem' }}>
                    <span>You receive</span>
                    <strong>
                      {formatCoins(quote.coins + quote.bonusCoins)}
                      {quote.bonusCoins > 0
                        ? ` (${formatCoins(quote.coins)} + ${formatCoins(quote.bonusCoins)} bonus)`
                        : ''}
                    </strong>
                  </div>
                )}
                {quote && !quoteError && (
                  <div style={rowStyle}>
                    <span>Total</span>
                    <span>{formatUsdCents(quote.usdCents)}</span>
                  </div>
                )}

                <div style={{ marginTop: '1rem' }}>
                  <Button
                    label={isPurchasing ? 'Starting checkout…' : 'Continue to checkout'}
                    disabled={!quote || isPurchasing || Boolean(quoteError)}
                    onClick={() => {
                      void handlePurchase();
                    }}
                  />
                </div>
                <p
                  style={{
                    color: 'var(--fm-color-text-muted)',
                    fontSize: '0.75rem',
                    marginTop: '0.75rem',
                  }}
                >
                  Payments are processor-agnostic. Dev uses a stub processor; production will plug in
                  an adult-friendly provider.
                </p>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}
